import { capitalize } from '@mui/material'
import moment from 'moment-timezone'
import React from 'react'

import Loading from '@/app/component/guard/loading'
import CampaignStatus from '@/app/module/campaigns/campaign-status'
import { type CampaignItem, CampaignTemplateType, type Delta, dayOfWeekMap } from '@/app/module/campaigns/types'
import type { LoadingError } from '@/app/types'

type Delay = Omit<Delta, 'relative' | 'unit' | 'value'> & {
  relative?: Delta['relative'] | null
  unit?: Delta['unit'] | null
  value: number | string
}

type Messages = {
  error?: LoadingError | null
  loading?: boolean
}

type Props = {
  errorMessages?: string[]
  isValid?: boolean
  item: CampaignItem
  loading?: boolean
  messages: Messages
  orgId?: number
  published: boolean
}

const CampaignConfirm: React.FC<Props> = ({
  errorMessages = [],
  isValid = true,
  item,
  loading,
  messages,
  orgId,
  published,
}) => {
  const { loading: msgLoading = false, error } = messages
  const delta = item.type === CampaignTemplateType.Drip ? item.variables.parts[0]?.delta : undefined

  const delayText = React.useMemo(() => parseDelay(delta), [delta])

  return (
    <Loading isLoading={!orgId || (!loading ? msgLoading : false)} error={error ?? undefined}>
      <CampaignStatus delay={delayText} errorMessages={errorMessages} isValid={isValid} published={published} />
    </Loading>
  )
}

const getFormattedDaysOfWeek = ({ dayOfWeek, daysOfWeek }: Delay) => {
  const selectedDays = Object.keys(daysOfWeek ?? {})
    .filter((day) => daysOfWeek?.[day])
    .sort((a, b) => (dayOfWeekMap[a] ?? Number.MAX_SAFE_INTEGER) - (dayOfWeekMap[b] ?? Number.MAX_SAFE_INTEGER))

  if (selectedDays.length) {
    return selectedDays.map(capitalize).join(', ')
  }

  return dayOfWeek ? capitalize(dayOfWeek) : ''
}

export const parseDelay = (delta?: Delay) => {
  if (!delta) {
    return undefined
  }

  const { unit, value, time, timezone } = delta
  const numericValue = Number(value)

  if (!unit || unit === 'immediate' || (numericValue === 0 && !time)) {
    return 'immediately'
  }

  if (!time) {
    return `${value} ${unit}${numericValue > 1 ? 's' : ''}`
  }

  const theFirst = numericValue < 1 ? 'the first ' : ''
  const formattedDaysOfWeek = getFormattedDaysOfWeek(delta)
  const timezoneText = timezone ? ` (${timezone})` : ''

  return `at ${`${unit === 'day' ? theFirst : ''}${moment(time, 'HH:mm').format('hh:mm A')}`}${timezoneText}${
    unit === 'week' && formattedDaysOfWeek ? `, on ${theFirst}${formattedDaysOfWeek}` : ''
  }${numericValue > 0 ? `, ${value} ${unit}${numericValue > 1 ? 's' : ''}` : ''}`
}

export default CampaignConfirm
